import type { ActorLookup } from "@istari-frontend/notifications/model";
import type { V3 } from "@istari/istari-client";
import { useMemo } from "react";

import { useSystemNames } from "~/systems/feature/use-system-names";
import { useUserDisplayNames } from "~/user/feature/use-user-display-names";
import { useWorkflowLogMetadata, workflowLogMetadataKey } from "~/workflow-log/feature/use-workflow-log-metadata";

import { toActorLookup } from "./actor-lookup";
import { useReportLookupFailures } from "./use-report-lookup-failures";

export type WorkflowLogDetail = { title: string | null; createdById: string | null };
export type WorkflowLogEnrichment = {
  workflowLogDetails: WorkflowLogDetail[];
  workflowLogCreatorNames: ActorLookup[];
  workflowLogSystemNames: (string | null)[];
};

export function useWorkflowLogEnrichment(
  events: V3.UserWorkflowLogCompletedNotificationEvent[],
): WorkflowLogEnrichment {
  const metadata = useWorkflowLogMetadata(
    events.map((event) => ({ systemId: event.systemId, entryId: event.workflowLogEntryId })),
  );
  const details = useMemo(
    () =>
      events.map((event) => {
        const result = metadata[workflowLogMetadataKey(event.systemId, event.workflowLogEntryId)];
        return {
          title: result?.isError ? null : (result?.data?.title ?? null),
          createdById: result?.isError ? null : (result?.data?.createdById ?? null),
        };
      }),
    [metadata, events],
  );
  const users = useUserDisplayNames(details.map((detail) => detail.createdById));
  const systems = useSystemNames(events.map((event) => event.systemId));
  useReportLookupFailures(
    "notifications-workflow-log-lookup-failed",
    events.flatMap((event) => {
      const result = metadata[workflowLogMetadataKey(event.systemId, event.workflowLogEntryId)];
      return result?.isError
        ? [{ error: result.error, context: { systemId: event.systemId, workflowLogEntryId: event.workflowLogEntryId } }]
        : [];
    }),
  );
  useReportLookupFailures(
    "notifications-workflow-log-creator-lookup-failed",
    Object.entries(users).flatMap(([userId, result]) =>
      result.status === "error" ? [{ error: result.error, context: { userId } }] : [],
    ),
  );
  useReportLookupFailures(
    "notifications-workflow-log-system-lookup-failed",
    Object.entries(systems).flatMap(([systemId, result]) =>
      result.isError ? [{ error: result.error, context: { systemId } }] : [],
    ),
  );
  const names = useMemo(
    () =>
      events.map((event, index) => {
        const result = metadata[workflowLogMetadataKey(event.systemId, event.workflowLogEntryId)];
        const createdById = details[index].createdById;
        return toActorLookup(createdById ? users[createdById] : undefined, !!result?.isLoading);
      }),
    [events, metadata, details, users],
  );
  const systemNames = useMemo(
    () =>
      events.map((event) => {
        const result = systems[event.systemId];
        return result?.isError ? null : (result?.data ?? null);
      }),
    [events, systems],
  );
  return { workflowLogDetails: details, workflowLogCreatorNames: names, workflowLogSystemNames: systemNames };
}
